import React, { useState, useEffect } from 'react';
import { useMarketData, useTrading, useConnection, useEngine } from '../context/MarketContext';
import { TIF_OPTIONS } from '../types/orders';

interface Props { prefillPrice?: number; }

export default function QuickOrderPanel({ prefillPrice }: Props) {
  const { currentPrice, orderBook, selectedSymbol, labelForSecId } = useMarketData();
  const { placeOrder } = useTrading();
  const { connectionStatus } = useConnection();
  const { engineConfig } = useEngine();

  const [quantity, setQuantity] = useState<string>('1000');
  const [price, setPrice] = useState<string>('');
  const [orderType, setOrderType] = useState<'LIMIT' | 'MARKET'>('LIMIT');
  const [tif, setTif] = useState<number>(engineConfig?.default_time_in_force ?? 1);
  const [expireDate, setExpireDate] = useState<string>('');
  const [expireTime, setExpireTime] = useState<string>('');
  const [submitConfirm, setSubmitConfirm] = useState<{ side: 'BUY' | 'SELL'; qty: number; price?: number } | null>(null);

  const bestBid = orderBook.bids.length > 0 ? orderBook.bids[0].price : null;
  const bestAsk = orderBook.asks.length > 0 ? orderBook.asks[0].price : null;

  // Sync TIF with global default when it changes (only if user hasn't customised it this session)
  const [tifTouched, setTifTouched] = useState(false);
  useEffect(() => {
    if (!tifTouched && engineConfig?.default_time_in_force != null) {
      setTif(engineConfig.default_time_in_force);
    }
  }, [engineConfig?.default_time_in_force, tifTouched]);

  // Auto-fill price: prefill prop wins; otherwise midpoint or current price
  useEffect(() => {
    if (prefillPrice != null) {
      setPrice(prefillPrice.toFixed(2));
      return;
    }
    const mid = bestBid && bestAsk
      ? ((bestBid + bestAsk) / 2)
      : (bestBid ?? bestAsk ?? currentPrice);
    if (mid > 0) setPrice(mid.toFixed(2));
  }, [prefillPrice, bestBid, bestAsk, currentPrice]);

  function submit(side: 'BUY' | 'SELL') {
    const qty = parseFloat(quantity);
    if (!qty || qty <= 0) { alert('Enter a valid quantity'); return; }
    if (orderType === 'LIMIT') {
      const px = parseFloat(price);
      if (!px || px <= 0) { alert('Enter a valid price for limit orders'); return; }
    }
    if (connectionStatus !== 'connected') { alert('Cannot place order — not connected'); return; }
    const clientOrderId = `ORD_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    placeOrder({
      clientOrderId,
      symbol: selectedSymbol,
      side,
      orderType,
      price: orderType === 'LIMIT' ? parseFloat(price) : undefined,
      quantity: qty,
      timeInForce: tif,
      expireDate: tif === 6 && expireDate ? expireDate.replace(/-/g, '') : undefined,
      expireTime: tif === 6 && expireTime ? expireTime : undefined,
    });
    setQuantity('1000');
    // keep price (user likely wants to trade again at similar level)
    setSubmitConfirm({ side, qty, price: orderType === 'LIMIT' ? parseFloat(price) : undefined });
    setTimeout(() => setSubmitConfirm(null), 3000);
  }

  const isConnected = connectionStatus === 'connected';

  return (
    <div style={{ background: '#10151b', borderRadius: 8, border: '1px solid #2a3540', overflow: 'hidden' }}>
      {/* Header row */}
      <div style={{ padding: '8px 12px', borderBottom: '1px solid #2a3540', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#e8a33d', letterSpacing: '0.08em' }}>QUICK ORDER</span>
          <span style={{ fontSize: 10, fontWeight: 600, color: '#7e8a96', backgroundColor: '#0a0d10', border: '1px solid #2a3540', borderRadius: 4, padding: '1px 6px' }}>
            {labelForSecId(selectedSymbol)}
          </span>
        </div>
        {/* LIMIT / MARKET toggle */}
        <div style={{ display: 'flex', gap: 4 }}>
          {(['LIMIT', 'MARKET'] as const).map(t => (
            <button
              key={t}
              onClick={() => setOrderType(t)}
              style={{
                padding: '3px 8px', fontSize: 10, fontWeight: 700, borderRadius: 4, cursor: 'pointer', border: 'none',
                backgroundColor: orderType === t ? '#4da3ff' : '#2a3540',
                color: orderType === t ? 'white' : '#7a8693',
              }}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {/* Order submission confirmation */}
        {submitConfirm && (
          <div style={{
            padding: '6px 10px', borderRadius: 4, fontSize: 11, fontWeight: 700,
            backgroundColor: submitConfirm.side === 'BUY' ? 'rgba(54,201,142,0.15)' : 'rgba(255,92,92,0.15)',
            color: submitConfirm.side === 'BUY' ? '#36c98e' : '#ff5c5c',
            border: `1px solid ${submitConfirm.side === 'BUY' ? '#36c98e' : '#ff5c5c'}40`,
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            ✓ {submitConfirm.side} {submitConfirm.qty.toLocaleString()}
            {submitConfirm.price != null ? ` @ ${submitConfirm.price.toFixed(2)}` : ' (MARKET)'} submitted
          </div>
        )}
        {/* Not connected warning */}
        {!isConnected && (
          <div style={{ padding: '6px 10px', background: '#7f1d1d', color: '#fca5a5', borderRadius: 4, fontSize: 10, textAlign: 'center' }}>
            Not connected — orders disabled
          </div>
        )}

        {/* Qty + Price row */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div>
            <label style={{ fontSize: 10, color: '#46525e', fontWeight: 700, letterSpacing: '0.06em', display: 'block', marginBottom: 4 }}>QTY</label>
            <input
              type="number"
              value={quantity}
              onChange={e => setQuantity(e.target.value)}
              placeholder="1000"
              style={{
                width: '100%', padding: '7px 10px', background: '#0a0d10', color: '#d7dde4',
                border: '1px solid #2a3540', borderRadius: 5, fontSize: 13, boxSizing: 'border-box',
              }}
            />
          </div>
          {orderType === 'LIMIT' ? (
            <div>
              <label style={{ fontSize: 10, color: '#46525e', fontWeight: 700, letterSpacing: '0.06em', display: 'block', marginBottom: 4 }}>PRICE</label>
              <input
                type="number"
                value={price}
                onChange={e => setPrice(e.target.value)}
                placeholder={currentPrice > 0 ? currentPrice.toFixed(2) : '—'}
                style={{
                  width: '100%', padding: '7px 10px', background: '#0a0d10', color: '#d7dde4',
                  border: '1px solid #2a3540', borderRadius: 5, fontSize: 13, boxSizing: 'border-box',
                }}
              />
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', paddingBottom: 2 }}>
              <span style={{ fontSize: 10, color: '#46525e', marginBottom: 4 }}>MKT PRICE</span>
              <span style={{ fontSize: 13, color: '#7e8a96', fontWeight: 600 }}>
                {currentPrice > 0 ? currentPrice.toFixed(2) : '—'}
              </span>
            </div>
          )}
        </div>

        {/* TIF selector */}
        <div>
          <label style={{ fontSize: 10, color: '#46525e', fontWeight: 700, letterSpacing: '0.06em', display: 'block', marginBottom: 4 }}>TIME IN FORCE</label>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {TIF_OPTIONS.map(({ v, label }) => (
              <button
                key={v}
                title={TIF_OPTIONS.find(o => o.v === v)?.desc}
                onClick={() => { setTif(v); setTifTouched(true); }}
                style={{
                  padding: '3px 8px', fontSize: 10, fontWeight: 700, borderRadius: 4, cursor: 'pointer', border: 'none',
                  backgroundColor: tif === v ? '#4da3ff' : '#2a3540',
                  color: tif === v ? 'white' : '#7a8693',
                }}
              >
                {label}
              </button>
            ))}
          </div>
          {tif === 6 && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginTop: 6 }}>
              <div>
                <label style={{ fontSize: 9, color: '#46525e', display: 'block', marginBottom: 2 }}>EXPIRE DATE</label>
                <input
                  type="date"
                  value={expireDate}
                  onChange={e => setExpireDate(e.target.value)}
                  style={{
                    width: '100%', padding: '5px 8px', background: '#0a0d10', color: '#d7dde4',
                    border: '1px solid #2a3540', borderRadius: 4, fontSize: 11, boxSizing: 'border-box',
                  }}
                />
              </div>
              <div>
                <label style={{ fontSize: 9, color: '#46525e', display: 'block', marginBottom: 2 }}>EXPIRE TIME (EAT)</label>
                <input
                  type="time"
                  value={expireTime}
                  onChange={e => setExpireTime(e.target.value + ':00')}
                  style={{
                    width: '100%', padding: '5px 8px', background: '#0a0d10', color: '#d7dde4',
                    border: '1px solid #2a3540', borderRadius: 4, fontSize: 11, boxSizing: 'border-box',
                  }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Best bid/ask hint */}
        {(bestBid || bestAsk) && (
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#46525e' }}>
            <span>Bid <span style={{ color: '#36c98e', fontWeight: 600 }}>{bestBid?.toFixed(2) ?? '—'}</span></span>
            <span>Ask <span style={{ color: '#ff5c5c', fontWeight: 600 }}>{bestAsk?.toFixed(2) ?? '—'}</span></span>
          </div>
        )}

        {/* BUY / SELL action buttons */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 2 }}>
          <button
            onClick={() => submit('BUY')}
            disabled={!isConnected}
            style={{
              padding: '10px 0', background: '#36c98e', color: 'white', border: 'none',
              borderRadius: 6, cursor: isConnected ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 700,
              opacity: isConnected ? 1 : 0.45, letterSpacing: '0.04em',
              boxShadow: isConnected ? '0 0 0 1px rgba(54,201,142,0.4)' : 'none',
            }}
          >
            BUY
          </button>
          <button
            onClick={() => submit('SELL')}
            disabled={!isConnected}
            style={{
              padding: '10px 0', background: '#ff5c5c', color: 'white', border: 'none',
              borderRadius: 6, cursor: isConnected ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 700,
              opacity: isConnected ? 1 : 0.45, letterSpacing: '0.04em',
              boxShadow: isConnected ? '0 0 0 1px rgba(255,92,92,0.4)' : 'none',
            }}
          >
            SELL
          </button>
        </div>
      </div>
    </div>
  );
}
